import { forwardRef } from "react";

import { cx, styled } from "simplydo/react/styled";
import { belowComputer, computerUp, mobileOnly } from "simplydo/react/theme";

import { gapCss, gridTemplate } from "./Grid.styles";
import type { GridProps } from "./Grid.types";
import { GridColumn } from "./GridColumn";

type GridStyle = {
  $columns: number;
  $computer?: number;
  $tablet?: number;
  $mobile?: number;
  $gap?: string | number;
};

const GridRoot = styled.div<GridStyle>`
  flex: 1;
  display: grid;
  grid-template-columns: ${({ $columns }) => gridTemplate($columns)};
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  gap: ${({ $gap }) => gapCss($gap)};
  grid-auto-rows: min-content;

  ${belowComputer} {
    &.is-tablet {
      grid-template-columns: ${({ $tablet }) =>
        $tablet != null ? gridTemplate($tablet) : undefined};
    }
    &.is-tablet-stack > ${GridColumn} {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 100%;
    }
  }

  ${computerUp} {
    &.is-computer {
      grid-template-columns: ${({ $computer }) =>
        $computer != null ? gridTemplate($computer) : undefined};
    }
    &.is-computer-stack > ${GridColumn} {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 100%;
    }
  }

  ${mobileOnly} {
    &.is-mobile {
      grid-template-columns: ${({ $mobile }) =>
        $mobile != null ? gridTemplate($mobile) : undefined};
    }
    &.is-mobile-stack > ${GridColumn} {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 100%;
    }
  }
`;

const GridComponent = forwardRef<HTMLDivElement, GridProps>((props, ref) => {
  const {
    columns = 1,
    computer,
    tablet,
    mobile,
    gap,
    children,
    className,
    style,
    ...rest
  } = props;
  return (
    <GridRoot
      ref={ref}
      className={cx(
        mobile != null && "is-mobile",
        mobile === 1 && "is-mobile-stack",
        tablet != null && "is-tablet",
        tablet === 1 && "is-tablet-stack",
        computer != null && "is-computer",
        computer === 1 && "is-computer-stack",
        className,
      )}
      style={style}
      $columns={columns}
      $computer={computer}
      $tablet={tablet}
      $mobile={mobile}
      $gap={gap}
      {...rest}
    >
      {children}
    </GridRoot>
  );
});

GridComponent.displayName = "Grid";

export const Grid = styled(GridComponent)``;
